import type { OperatorPortalConfig } from './operatorPortalConfig'

/**
 * "Come si carica sul portale": i passi per portare la pratica dal CRM al portale dell'operatore, con cosa fa il CRM
 * da solo e cosa resta da fare a mano. Il passo già fatto è segnato; il primo da fare è in grassetto.
 */
export interface PortalUploadGuideProps {
  config: OperatorPortalConfig
  isErmes: boolean
  /** Dati per il portale ancora mancanti (vuoto = completi). */
  missing: string[]
  /** PDA firmata tra gli allegati (solo portali che la chiedono). */
  pdaLoaded: boolean
  status: string
  /** Codice della pratica sul portale (assegnato dopo il caricamento). */
  portalCode: string | null
  /** Portali Coraly: avviso del server quando il collegamento è sospeso (la card la crea il backoffice a mano). */
  coralySuspended?: string | null
}

interface Step { text: string; done: boolean }

/**
 * Portali Coraly su cui il CRM crea la card da solo («Carica sul portale», 31/10/2026): oggi Sky (processo «Sky | Consumer»,
 * stesso elenco del server, CoralyProcesses). Per gli altri operatori Coraly la card la crea ancora il backoffice a mano.
 */
export function coralyUploadFromCrm(portal: string | undefined): boolean {
  return portal === 'Coraly · Sky'
}

export function portalUploadSteps(p: PortalUploadGuideProps): { portal: string; steps: Step[]; after: string } {
  const portal = p.isErmes ? 'Ermes' : p.config.profile?.portal ?? 'dell\'operatore'
  const dati: Step = {
    text: p.missing.length === 0 ? 'Dati per il portale completi.' : `Completa i dati per il portale (mancano ${p.missing.length}).`,
    done: p.missing.length === 0,
  }
  const inviata = p.status !== 'Bozza' && p.status !== 'IntegrazioneRichiesta'
  const caricata = !!p.portalCode

  if (p.isErmes) return {
    portal, after: 'Il CRM compila anche «Altro» (sempre la prima voce) e «Allegati»: aggiunge le operazioni sul sito scelte qui, carica bolletta e documenti convertendo i PDF in immagini (Ermes accetta solo immagini). Stato e motivo di sospensioni, KO e annullamenti si aggiornano da soli leggendo Ermes; ogni POD/PDR del contratto ha la sua pratica.',
    steps: [
      dati,
      { text: 'Invia la pratica al backoffice (lo staff può saltare questo passo).', done: inviata || caricata },
      { text: 'Lo staff preme «Carica su Ermes» qui sotto: il CRM crea la bozza ContrattiSmart e scrive qui il codice.', done: caricata },
      { text: 'Dati corretti qui dopo la creazione? «Aggiorna la bozza su Ermes» li rimanda sul portale finché la bozza è modificabile.', done: false },
      { text: 'Controllata la bozza (operazioni sul sito, allegati), «Invia alla firma OTP» qui sotto: il cliente firma col codice via SMS/email e la pratica passa a «In attesa di firma». Il CRM controlla Ermes da solo ogni 10 minuti: a firma conclusa la pratica passa a «In lavorazione operatore» e contratto firmato e log della firma arrivano negli allegati.', done: false },
    ],
  }
  if (p.config.signedPda) return {
    portal, after: 'Dopo il caricamento stato e motivo arrivano dal portale; l\'esito di ogni invio è nelle note interne.',
    steps: [
      dati,
      { text: 'Carica tra gli allegati la PDA firmata dal cliente.', done: p.pdaLoaded },
      { text: 'Invia la pratica al backoffice: il CRM la carica su Teamwork da solo (o con «Carica su Teamwork»).', done: inviata },
      { text: 'Il codice Teamwork compare qui quando il portale la accetta.', done: caricata },
    ],
  }
  if (coralyUploadFromCrm(portal) && p.coralySuspended) return {
    portal, after: p.coralySuspended,
    steps: [
      dati,
      { text: 'Invia la pratica al backoffice (lo staff può saltare questo passo).', done: inviata || caricata },
      { text: 'Il caricamento automatico è sospeso: il backoffice crea la card sul portale a mano, con questi dati, e scrive qui il codice.', done: caricata },
      { text: 'Anche l\'aggiornamento delle fasi dal portale è sospeso: lo stato della pratica si aggiorna a mano.', done: false },
    ],
  }
  if (coralyUploadFromCrm(portal)) return {
    portal, after: 'Con la card collegata, la fase del portale aggiorna lo stato della pratica (in lavorazione, attivata, KO, sospesa) e i messaggi in chat arrivano all\'agente: scheda «Portale» qui sotto. Gli allegati della pratica (documento, moduli) vanno ancora caricati sulla card dal portale.',
    steps: [
      dati,
      { text: 'Premi «Carica sul portale» qui sotto (l\'agente della pratica o lo staff): il CRM crea la card con questi dati e il prodotto, la collega e la pratica passa a «Bozza operatore creata», senza passare dal backoffice Equal.', done: caricata },
      { text: 'Da qui la pratica segue le fasi del portale: verifica dei documenti, firma digitale, inserimento, attivazione.', done: false },
    ],
  }
  if (portal.startsWith('Coraly')) return {
    portal, after: 'Con la card collegata, la fase del portale aggiorna lo stato della pratica e i messaggi in chat arrivano all\'agente.',
    steps: [
      dati,
      { text: 'Invia la pratica al backoffice.', done: inviata },
      { text: `Il backoffice crea la card su ${portal} con questi dati (a mano: per questo operatore il CRM non crea ancora la card).`, done: caricata },
      { text: 'Collega la card nella scheda «Coraly» di questa pratica.', done: caricata },
    ],
  }
  return {
    portal, after: 'Finché il portale non ha un collegamento automatico, l\'esito si registra a mano o con l\'import esiti.',
    steps: [
      dati,
      { text: 'Invia la pratica al backoffice.', done: inviata },
      { text: `Il backoffice la carica sul portale ${portal} (a mano) e scrive qui il codice del portale.`, done: caricata },
    ],
  }
}
